/**
 * pomodoro-timer — pure timer state machine for the Pomodoro overlay.
 *
 * No React, no clock access: every function takes `now` explicitly so the
 * maths is trivially testable. Time is derived from `endsAt` (an absolute
 * epoch-ms deadline), never from counting ticks, so the countdown stays
 * accurate in background tabs and across reloads.
 */

export type PomodoroMode = 'pomodoro' | 'short' | 'long';
export type PomodoroStatus = 'idle' | 'running' | 'paused';

export const MODE_DURATIONS_MS: Record<PomodoroMode, number> = {
  pomodoro: 25 * 60_000,
  short: 5 * 60_000,
  long: 15 * 60_000,
};

export const MODE_LABELS: Record<PomodoroMode, string> = {
  pomodoro: 'Pomodoro',
  short: 'Short Break',
  long: 'Long Break',
};

export interface TimerState {
  mode: PomodoroMode;
  status: PomodoroStatus;
  /** Epoch-ms deadline while running; null otherwise. */
  endsAt: number | null;
  /** Time left while idle/paused (the full duration when idle). */
  remainingMs: number;
}

/** Idle timer at the full duration of `mode`. */
export function initialTimer(mode: PomodoroMode = 'pomodoro'): TimerState {
  return { mode, status: 'idle', endsAt: null, remainingMs: MODE_DURATIONS_MS[mode] };
}

/** idle | paused → running. A running timer is returned unchanged. */
export function start(s: TimerState, now: number): TimerState {
  if (s.status === 'running') return s;
  return { ...s, status: 'running', endsAt: now + s.remainingMs };
}

/** running → paused, freezing the remaining time. Other states unchanged. */
export function pause(s: TimerState, now: number): TimerState {
  if (s.status !== 'running' || s.endsAt === null) return s;
  return { ...s, status: 'paused', endsAt: null, remainingMs: Math.max(0, s.endsAt - now) };
}

/** Back to idle on the same mode, full duration. */
export function reset(s: TimerState): TimerState {
  return initialTimer(s.mode);
}

/** Idle on a new mode, full duration. */
export function switchMode(_s: TimerState, mode: PomodoroMode): TimerState {
  return initialTimer(mode);
}

export function remainingAt(s: TimerState, now: number): number {
  if (s.status === 'running' && s.endsAt !== null) return Math.max(0, s.endsAt - now);
  return s.remainingMs;
}

export function isFinished(s: TimerState, now: number): boolean {
  return s.status === 'running' && s.endsAt !== null && now >= s.endsAt;
}

/** "25:00" / "04:09" / "00:00". Rounds UP to whole seconds; never negative. */
export function formatClock(ms: number): string {
  const totalSeconds = Math.ceil(Math.max(0, ms) / 1000);
  const m = Math.floor(totalSeconds / 60);
  const sec = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}
